import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { featureList } from '../data/featuresContent';

const Footer = () => {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-900 text-white pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
          <div>
            <div className="bg-blue-600 rounded-lg px-4 py-2 inline-block mb-4">
              <span className="font-bold text-xl">RentKollect</span>
              <p className="text-[10px] leading-tight">{t('nav.tagline')}</p>
            </div>
            <p className="text-gray-400 mb-4">{t('ft.blurb')}</p>
            <div className="flex gap-3 text-xs">
              <a href="mailto:hello@rentkollect.com" data-testid="footer-social-email" className="flex items-center gap-2 bg-gray-800 hover:bg-emerald-600 px-3 py-2 rounded-lg transition-colors">
                <Mail size={14} />
                <span>hello@rentkollect.com</span>
              </a>
            </div>
          </div>

          <div>
            <h3 className="font-bold text-lg mb-4">Product</h3>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/features" data-testid="footer-link-features-all" className="hover:text-white transition-colors font-semibold text-gray-200">All features →</Link></li>
              {featureList.map((f) => (
                <li key={f.slug}>
                  <Link
                    to={`/features/${f.slug}`}
                    data-testid={`footer-link-feature-${f.slug}`}
                    className="hover:text-white transition-colors"
                  >
                    {f.eyebrow}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-lg mb-4">{t('ft.quick_links')}</h3>
            <ul className="space-y-2 text-gray-400">
              <li><a href="/privacy" data-testid="footer-link-privacy" className="hover:text-white transition-colors">{t('ft.link_privacy')}</a></li>
              <li><a href="/terms" data-testid="footer-link-terms" className="hover:text-white transition-colors">{t('ft.link_terms')}</a></li>
              <li><a href="/refund" data-testid="footer-link-refund" className="hover:text-white transition-colors">Cancellation & Refund Policy</a></li>
              <li><a href="/data-deletion" data-testid="footer-link-data-deletion" className="hover:text-white transition-colors">{t('ft.link_data_deletion')}</a></li>
              <li><a href="/contact" data-testid="footer-link-contact" className="hover:text-white transition-colors">{t('ft.link_contact')}</a></li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-lg mb-4">{t('ft.contact')}</h3>
            <ul className="space-y-3 text-gray-400">
              <li className="flex items-center gap-2">
                <Mail size={18} />
                <a href="mailto:hello@rentkollect.com" className="hover:text-white transition-colors">hello@rentkollect.com</a>
              </li>
              <li className="flex items-center gap-2">
                <Phone size={18} />
                <a href="https://wa.me/919821922296" target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="hover:text-white transition-colors">+91 98219 22296 (WhatsApp)</a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin size={18} className="mt-1 flex-shrink-0" />
                <span>{t('ft.address')}</span>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-lg mb-4">{t('ft.app_title')}</h3>
            <p className="text-xs text-gray-400 mb-3">{t('ft.app_waitlist')}</p>
            <div className="space-y-3">
              <div data-testid="footer-play-store-btn" className="relative block cursor-not-allowed" aria-disabled="true" title={t('ft.soon')}>
                <div className="bg-gray-800 rounded-lg p-3 flex items-center gap-3 opacity-60">
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="#34A853">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                  </svg>
                  <div>
                    <p className="text-xs text-gray-400">{t('ft.play_top')}</p>
                    <p className="font-semibold">{t('ft.play_bottom')}</p>
                  </div>
                </div>
                <span className="absolute -top-2 -right-2 bg-amber-400 text-gray-900 text-[10px] font-bold px-2 py-0.5 rounded-full border border-amber-300 shadow">{t('ft.soon')}</span>
              </div>
              <div data-testid="footer-app-store-btn" className="relative block cursor-not-allowed" aria-disabled="true" title={t('ft.soon')}>
                <div className="bg-gray-800 rounded-lg p-3 flex items-center gap-3 opacity-60">
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="white">
                    <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" />
                  </svg>
                  <div>
                    <p className="text-xs text-gray-400">{t('ft.app_top')}</p>
                    <p className="font-semibold">{t('ft.app_bottom')}</p>
                  </div>
                </div>
                <span className="absolute -top-2 -right-2 bg-amber-400 text-gray-900 text-[10px] font-bold px-2 py-0.5 rounded-full border border-amber-300 shadow">{t('ft.soon')}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-8 pb-8 border-b border-gray-800">
          <h4 className="font-semibold mb-2 flex items-center gap-2">
            <MapPin size={18} className="text-blue-500" />
            {t('ft.head_office')}
          </h4>
          <p className="text-gray-400 text-sm">
            {t('ft.hq_line1')}<br />
            {t('ft.hq_line2')}
          </p>
        </div>

        <div className="text-center text-gray-400 text-sm space-y-2">
          <p className="text-[13px] text-gray-300" data-testid="footer-legal-entity">{t('ft.legal_entity')}</p>
          <p>{t('ft.rights', currentYear)}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
